import { useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { LegendDatum, ResponsiveWaffle } from '@nivo/waffle'
import { AttributeValue, Total } from '@ui/interfaces'
import { useTelemetryStore } from '@ui/stores'

import { Legend } from './Legend'
import { Tooltip } from './Tooltip'

interface WaffleV2Props {
  chartData: AttributeValue[]

  selectedKey: string
  total: Total
}

type LegendDatumWithPercentage = LegendDatum & { percentage: number }

export const WaffleV2 = ({ chartData, selectedKey, total }: WaffleV2Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const hasSetLegendData = useRef<boolean>(false)

  const height = containerRef.current?.offsetHeight
  const width = containerRef.current?.offsetWidth

  const [legendData, setLegendData] = useState<LegendDatumWithPercentage[]>([])

  const { selectedValue, setSelectedValue, setWhereClause } = useTelemetryStore(
    useShallow((state) => ({
      selectedValue: state.selectedValue,
      setSelectedValue: state.setSelectedValue,
      setWhereClause: state.setWhereClause,
    }))
  )

  const handleClick = (id: string | number) => {
    const selectedData = chartData.find((item) => item.value === id)
    if (!selectedData) return

    let { value } = selectedData.queryProps
    value = value === '' ? "''" : value.replace(/(?!^)'(?!$)/g, "''").replace(/^'|'$/g, '"')

    const prefix = selectedKey.match(/^(\w+)\.(.*)/) || []

    if (prefix.length > 2) {
      if (value === 'null') {
        setWhereClause(`missing:${prefix[1]}[${prefix[2]}]`)
        return
      }
      setWhereClause(`${prefix[1]}[${prefix[2]}]:${value}`)
    } else {
      if (value === 'not null') {
        setWhereClause(`has:${selectedKey}`)
        return
      }
      if (value === 'null') {
        setWhereClause(`missing:${selectedKey}`)
        return
      }
      setWhereClause(`${selectedKey}:${value}`)
    }
  }

  const handleLegendData = (data: LegendDatum[]) => {
    if (data.length > 0 && !hasSetLegendData.current) {
      const legendWithPercentage = data.map((item) => ({
        ...item,
        percentage: (item.data.value / total.count) * 100,
      }))

      setLegendData(legendWithPercentage)
      hasSetLegendData.current = true
    }
  }

  const waffleData = useMemo(() => {
    return chartData.map((item) => ({
      id: item.value,
      label: item.value,
      value: item.count,
    }))
  }, [chartData])

  const columns = useMemo(() => {
    return containerRef.current ? Math.floor(containerRef.current.offsetWidth / 40) : 40
  }, [containerRef.current])

  return (
    <div className="relative flex size-full flex-col" ref={containerRef}>
      <div className="flex w-full flex-row justify-between">
        <Tooltip selectedKey={selectedKey} selectedValue={selectedValue} total={total} />
      </div>

      <div className="flex h-[calc(100%-10rem)] w-full flex-row">
        <div
          style={{ height: height ? height * 0.65 : 0, width: width ? width * 0.72 : 0 }}
          className="relative flex cursor-pointer justify-start">
          <ResponsiveWaffle
            data={waffleData}
            total={total.count}
            rows={4}
            columns={columns}
            padding={1}
            fillDirection="bottom"
            valueFormat=".2f"
            margin={{ top: 0, right: 10, bottom: 0, left: 0 }}
            colors={{ scheme: 'nivo' }}
            borderRadius={3}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.3]],
            }}
            motionStagger={2}
            onClick={(data) => handleClick(data.id)}
            onMouseEnter={(data) => {
              const matchingItem = chartData.find((item) => item.value === data.id)
              if (matchingItem) {
                setSelectedValue(matchingItem)
              }
            }}
            onMouseLeave={() => {
              setSelectedValue({
                key: '',
                value: '',
                count: 0,
                size: 0,
                ratio: 0,
                queryProps: { operator: '', value: '' },
              })
            }}
            forwardLegendData={handleLegendData}
            tooltip={() => (
              <div className="rounded-md border border-gray-200 bg-white p-3.5 shadow-md">
                <Tooltip selectedKey={selectedKey} selectedValue={selectedValue} total={total} />
              </div>
            )}
          />
        </div>
        <div style={{ width: width ? width * 0.28 : 0 }} className="relative">
          <Legend data={legendData} onItemClick={handleClick} />
        </div>
      </div>
    </div>
  )
}
